import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Brain, User, Bell, Download, CreditCard, Shield, Palette, Accessibility, HelpCircle, FileDown, Trash2, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/settings/")({
  head: () => ({
    meta: [
      { title: "Settings · KAfrequency" },
      { name: "description", content: "Manage your profile, membership, notifications, downloads, and privacy." },
    ],
  }),
  component: SettingsHub,
});

type Item = {
  to: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  desc: string;
};

const GROUPS: { title: string; items: Item[] }[] = [
  {
    title: "Account",
    items: [
      { to: "/account", icon: User, label: "Profile", desc: "Name, photo, display preferences" },
      { to: "/account", icon: CreditCard, label: "Membership & billing", desc: "Plan, invoices, payment methods" },
      { to: "/account", icon: Shield, label: "Privacy & security", desc: "Sign-in, sessions, password" },
    ],
  },
  {
    title: "Experience",
    items: [
      { to: "/settings/notifications", icon: Bell, label: "Notifications", desc: "Daily activation, gates, releases, quiet hours" },
      { to: "/settings/privacy", icon: Shield, label: "Privacy & personalization", desc: "AI personalization, timeline, data export" },
      { to: "/settings/memory", icon: Brain, label: "Memory", desc: "What KA Intelligence remembers — off by default" },
      { to: "/downloads", icon: Download, label: "Downloads", desc: "Offline music, teachings, journal drafts" },
      { to: "/account", icon: Palette, label: "Appearance & audio", desc: "Theme, autoplay, background playback" },
      { to: "/account", icon: Accessibility, label: "Accessibility", desc: "Reduced motion, captions, text size" },
    ],
  },
  {
    title: "Data & support",
    items: [
      { to: "/account", icon: FileDown, label: "Export my data", desc: "Journal, progress, purchases" },
      { to: "/contact", icon: HelpCircle, label: "Help & feedback", desc: "Reach the temple team" },
      { to: "/account", icon: Trash2, label: "Delete account", desc: "Remove account and personal data" },
    ],
  },
];

function SettingsHub() {
  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <header>
        <h1 className="font-serif text-3xl text-gold-luminous">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">One place for your account, experience, and data.</p>
      </header>

      {GROUPS.map((g) => (
        <section key={g.title} aria-labelledby={`sec-${g.title}`} className="space-y-2">
          <h2 id={`sec-${g.title}`} className="px-1 text-xs uppercase tracking-[0.25em] text-gold/80">{g.title}</h2>
          <Card className="divide-y hairline overflow-hidden p-0">
            {g.items.map((it) => {
              const Icon = it.icon;
              return (
                <Link
                  key={it.label}
                  to={it.to}
                  className="flex min-h-14 items-center gap-3 p-4 text-left hover:bg-charcoal/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold"
                >
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border hairline text-gold">
                    <Icon size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm text-ivory">{it.label}</span>
                    <span className="block truncate text-xs text-muted-foreground">{it.desc}</span>
                  </span>
                  <ChevronRight size={16} className="shrink-0 text-muted-foreground" aria-hidden />
                </Link>
              );
            })}
          </Card>
        </section>
      ))}
    </div>
  );
}
